'use client';

import { useTranslations } from 'next-intl';
import { parseAsBoolean, useQueryState } from 'nuqs';
import { useEffect, useRef, useState } from 'react';
import Toast from '@/components/ui/Toast/toast';
import { Env } from '@/consts/Env';
import { enableOneSignalPushFromGesture, getPushSupportState, isOneSignalSdkReady, subscribeToOneSignalReady } from './oneSignal.client';

interface Props {
  user?: { id?: string; email?: string };
}

export function OneSignalPushNotificationQueryListener({ user }: Props) {
  const t = useTranslations('pwa');
  const [pushNotification, setPushNotification] = useQueryState('pushNotification', parseAsBoolean);
  const shownToastReasonRef = useRef<string | null>(null);
  const actionToastShownRef = useRef(false);
  const [isSdkReady, setIsSdkReady] = useState(() => isOneSignalSdkReady());
  const appId = Env.NEXT_PUBLIC_ONE_SIGNAL_APP_ID;

  useEffect(() => {
    if (isOneSignalSdkReady()) {
      setIsSdkReady(true);
      return;
    }

    const unsubscribe = subscribeToOneSignalReady(() => {
      setIsSdkReady(true);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!pushNotification || typeof window === 'undefined' || !appId) {
      actionToastShownRef.current = false;
      return;
    }

    const supportState = getPushSupportState();
    if (!supportState.supported) {
      if (shownToastReasonRef.current === supportState.reason) {
        return;
      }

      shownToastReasonRef.current = supportState.reason;

      const isIOSStandaloneRequired = supportState.reason === 'ios_not_standalone';
      Toast({
        type: 'warning',
        title: isIOSStandaloneRequired ? t('push-ios-standalone-title') : t('push-unsupported-title'),
        description: isIOSStandaloneRequired ? t('push-ios-standalone-description') : t('push-unsupported-description'),
      });
      void setPushNotification(null);
      return;
    }

    if (!isSdkReady) {
      return;
    }

    if (actionToastShownRef.current) {
      return;
    }

    // TEMP DISABLED (related to "Never miss a Free Bonus" toast):
    // This gate depends on the Congrats-modal "Never miss a Free Bonus" toast, which is
    // temporarily disabled. Functionality is preserved here, temporarily disabled.
    // // Skip if the user hasn't gone through the Congrats modal onboarding yet.
    // // New users see "Never miss a Free Bonus" push prompt there; showing this toast
    // // simultaneously (when ?pushNotification=true is in a sign-up URL) creates duplicates.
    // const hasSeenCongratsToast = typeof window !== 'undefined'
    //   && localStorage.getItem('congrats-toast-shown') === 'true';
    // if (!hasSeenCongratsToast) {
    //   void setPushNotification(null);
    //   return;
    // }

    actionToastShownRef.current = true;

    Toast({
      type: 'warning',
      title: t('enable-title'),
      description: t('enable-description'),
      acceptButtonText: t('enable-action'),
      rejectButtonText: t('enable-cancel'),
      onAccept: async () => {
        try {
          const result = await enableOneSignalPushFromGesture({ id: user?.id, email: user?.email });
          if (!result.enabled) {
            if (result.reason === 'permission_denied') {
              Toast({
                type: 'warning',
                title: t('push-permission-title'),
                description: t('push-permission-description'),
              });
              return;
            }

            Toast({
              type: 'error',
              title: t('push-error-title'),
              description: t('push-error-description'),
            });
          }
        } catch {
          Toast({
            type: 'error',
            title: t('push-error-title'),
            description: t('push-error-description'),
          });
        } finally {
          actionToastShownRef.current = false;
          void setPushNotification(null);
        }
      },
      onReject: () => {
        actionToastShownRef.current = false;
        void setPushNotification(null);
      },
      disableTimer: true,
    });
  }, [appId, isSdkReady, pushNotification, setPushNotification, t, user?.email, user?.id]);

  return null;
}
